import { useEffect, useState, useMemo } from "react";
import { Button, Row, Col, Container, FormFeedback, FormGroup, Input, FormText, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CustomSelect from "../../../components/CustomSelect";
import _ from 'lodash';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "../../../hooks/useTheme";
import DataTable from "../../../components/DataTable";
import DialogBox from "../../../components/DialogBox";
import ItemTypeCustomAttributes from './ItemTypeCustomAttributes';
import { fetchAuditByFilter } from "../../audit/data/auditService";
import { fetchUsers } from "../../users/data/userService";
import { formatDateTime } from "../../../utils/dateUtils";
import { TextInput, FormattedNumberField } from "../../../components/DataInputs";
import { getAllItemLookupTypes } from "../data/itemLookupTypeService";
import { fetchTags } from "../../tags/data/tagService";
import { fetchSuppliers } from "../../suppliers/data/supplierService";
import { getItemErrors } from "../../quotes/functions/quoteFunctions";
import { fetchSalesforceCodes } from "../../salesforceCodes/data/salesforceCodeService";

const BasketItemModal = ({ basketItemModalIsOpen, setBasketItemModalIsOpen, item: initialItem, onItemChange }) => {
    const theme = useTheme();

    // State for current active Tab
    const [currentActiveTab, setCurrentActiveTab] = useState("Details");

    // State for item (to update the displayed item dynamically)
    const [item, setItem] = useState(structuredClone(initialItem));
    const [originalItem, setOriginalItem] = useState(structuredClone(initialItem));
    const [itemErrors, setItemErrors] = useState(getItemErrors(initialItem, []));

    // dialog box state
    const [isDialogBoxOpen, setIsDialogBoxOpen] = useState(false);

    const customStyles = {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
    };

    const { 
        data: salesForceCodesData,
        isLoading: isLoadingSalesForceCodes,
        error: salesForceCodesError,
    } = useQuery({
        queryKey: ["salesforceCodes"],
        queryFn: fetchSalesforceCodes,
        staleTime: 60000
    });    

    const sfCodes = salesForceCodesData?.filter(x => !x.disabled) || [];  
    
    const upfrontSalesforceCodes = [
        ...(item?.isUpfront && item?.upfrontSalesforceCode && !sfCodes.some(x => (x.type === "Upfront" || (x.type === "Recurring" && x.canBePaidUpfront)) && x.name === item.upfrontSalesforceCode) ? [{ name: item.upfrontSalesforceCode, label: item.upfrontSalesforceCode + " (INVALID)" }] : []),
        ...(sfCodes.filter(x => x.type === "Upfront" || (x.type === "Recurring" && x.canBePaidUpfront)) ?? [])
    ]
    const recurringSalesforceCodes = [
        ...(item?.isRecurring && item?.recurringSalesforceCode && !sfCodes.filter(x => x.type === "Recurring").find(x => x.name === item.recurringSalesforceCode) ? [{ name: item.recurringSalesforceCode, label: item.recurringSalesforceCode + " (INVALID)" }] : []),
        ...(sfCodes.filter(x => x.type === "Recurring") ?? [])
    ];    

    useEffect(() => {
        const errors = getItemErrors(item, sfCodes)
        console.log('basketItem', item)
        console.log("errors", errors)
        setItemErrors(errors)
    }, [item])

    /*
        DATABASE QUERIES
    */

    // Fetch Item Lookup Types using useQuery
    const {
        data: itemLookupTypesData,
        isLoading: isLoadingItemLookupTypes,
        error: itemLookupTypesError,
    } = useQuery({
        queryKey: ["itemLookupTypesFilter"],
        queryFn: getAllItemLookupTypes,
        staleTime: 60000
    });

    // Fetch Tags using useQuery
    const {
        data: tagsData,
        isLoading: isLoadingTags,
        error: tagsError,
    } = useQuery({
        queryKey: ["tagsFilter"],
        queryFn: fetchTags,
        staleTime: 60000
    });

    // Fetch Suppliers using useQuery
    const {
        data: suppliersData,
        isLoading: isLoadingSuppliers,
        error: suppliersError,
    } = useQuery({
        queryKey: ["itemLookupSuppliersFilter"],
        queryFn: fetchSuppliers,
        staleTime: 60000
    });

    const getUsersLookup = async () => {
        const users = await fetchUsers();
        return users.map(user => ({
            ...user,
            fullName: `${user.firstName} ${user.lastName}`
        }))
    }

    const {
        data: usersLookup,
        isLoading: isLoadingUsersLookup,
        error: usersLookupError,
    } = useQuery({
        queryKey: ["itemLookupUsers"],
        queryFn: getUsersLookup,
        staleTime: 60000
    });


    const getAudit = async () => {
        const filter = { "pageNumber": 0, "pageSize": 0, "source": `quoteItem.${item.id}` }
        const audits = await fetchAuditByFilter(filter);

        return audits.auditItems.map((audit) => {
            const user = usersLookup.find((user) => user?.userGuid === audit.userGuid);
            return {
                ...audit,
                timestamp: formatDateTime(audit.timestamp),
                user: user ? `${user.firstName} ${user.lastName}` : "Unknown",
            };
        });
    }

    const {
        data: auditData,
        isLoading: isLoadingAudits,
        error: auditError,
        refetch: refetchAudits,
    } = useQuery({
        queryKey: ["BasketItemAudits", item.id],
        queryFn: getAudit,
        enabled: !!usersLookup, // Only run when usersLookup are available
        staleTime: 60000
    });

    const itemLookupTypeOptions = itemLookupTypesData?.map((item) => ({
        value: item.id,
        label: item.itemLookupTypeName
    })) || [];

    // Calculated Property.  Passed to the Module Inputs modal, so it knows what inputs to show, and what logic to run
    const selectedItemLookupType = useMemo(
        () => itemLookupTypesData?.find(x => x.id === item.itemLookupTypeId),
        [itemLookupTypesData, item.itemLookupTypeId]
    );

    useEffect(() => {
        if (usersLookup) {
            refetchAudits();
        }
    }, [usersLookup]);

    const handleUpfrontSalesforceCodeChange = (e) => {
        const newValue = e?.value ?? ""

        setItem(prevItem => {
            const recurringCode = prevItem.recurringSalesforceCode ?? "";
            const salesforceCode = prevItem.isRecurring && recurringCode && recurringCode !== newValue
                ? `${newValue} / ${recurringCode}`
                : newValue;
            return { ...prevItem, upfrontSalesforceCode: newValue, salesforceCode };
        });
    }

    const handleRecurringSalesforceCodeChange = (e) => {
        const newValue = e?.value ?? ""

        setItem(prevItem => {
            const upfrontCode = prevItem.upfrontSalesforceCode ?? "";
            const salesforceCode = prevItem.isUpfront && upfrontCode && upfrontCode !== newValue
                ? `${upfrontCode} / ${newValue}`
                : newValue;
            return { ...prevItem, recurringSalesforceCode: newValue, salesforceCode };
        });
    }    

    // Handle input changes
    const handleChange = (e, actionMeta) => {
        // react-select / CustomSelect path
        if (actionMeta && actionMeta.name) {
            const fieldName = actionMeta.name;
            const opt = e; // may be { value, label } or null when clearing

            setItem(prevItem => {
                const isChangingLookupType = fieldName === "itemLookupTypeId";
                const newItemLookupTypeId = isChangingLookupType ? Number(opt?.value ?? null) : prevItem.itemLookupTypeId;

                const currentLookupTypeName =
                    itemLookupTypesData?.find(x => x.id === prevItem.itemLookupTypeId)?.itemLookupTypeName || "";
                const newLookupTypeName =
                    itemLookupTypesData?.find(x => x.id === newItemLookupTypeId)?.itemLookupTypeName || "";

                const isQuoteGroupDescDefault =
                    prevItem.quoteGroupDescription === "" || prevItem.quoteGroupDescription === currentLookupTypeName;

                return {
                    ...prevItem,
                    // keep Group Name in sync with Type until user customizes it
                    quoteGroupDescription:
                        isChangingLookupType && isQuoteGroupDescDefault ? newLookupTypeName : prevItem.quoteGroupDescription,

                    // set selected value or null when cleared
                    [fieldName]: opt?.value ?? null,
                };
            });

            return; // important — don’t fall through to the native input path
        }

        // Native inputs path
        if (e && e.target) {
            const { name, value, type, checked } = e.target;
            const itemProperty = name.replace("unlocked_", "");

            setItem(prevItem => {
                const isChangingInternalName = name === "internalName";

                return {
                    ...prevItem,
                    upfrontNameOnQuote:
                        isChangingInternalName && prevItem.upfrontNameOnQuote === prevItem.internalName
                            ? value
                            : (name === "isUpfront" && checked && !prevItem.upfrontNameOnQuote)
                                ? prevItem.internalName
                                :  prevItem.upfrontNameOnQuote,
                    recurringNameOnQuote:
                        isChangingInternalName && prevItem.recurringNameOnQuote === prevItem.internalName
                            ? value
                            : (name === "isRecurring" && checked && !prevItem.recurringNameOnQuote)
                                ? prevItem.internalName
                                : prevItem.recurringNameOnQuote,

                    [itemProperty]: type === "checkbox" ? checked : value,
                };
            });

            return;
        }

        // Fallback (unexpected)
        console.warn("handleChange called without actionMeta or native event:", e, actionMeta);
    };

    const limitDecimals = (value, maxDecimals) => {
        const valueToString = value.toString();
        return parseFloat(valueToString.slice(0, valueToString.indexOf(".") + (maxDecimals + 1)));
    }

    const handleTypeCustomAttributesChange = (e, type, maxDecimals = null) => {
        let typeCustomAttributes = item.typeCustomAttributes ? [...item.typeCustomAttributes] : [];
        const label = e.target.name;
        let newValue;

        if (maxDecimals !== null) {
            e.target.value = limitDecimals(e.target.value, maxDecimals);
        }

        switch (type) {
            case 'integer':
                newValue = e.target.value === '' ? '' : e.target.valueAsNumber;
                break;
            case 'boolean':
                newValue = e.target.checked;
                break;
            case 'decimal':
                newValue = parseFloat(e.target.value) || 0;
                break;
            default:
                newValue = e.target.value;
        }

        if (newValue === '' && (type === 'text' || type === 'integer' || type === 'lookup')) {
            typeCustomAttributes = typeCustomAttributes.filter(attr => attr.label !== label);
        } else {
            const index = typeCustomAttributes.findIndex(attr => attr.label === label);
            if (index !== -1) {
                typeCustomAttributes[index] = {
                    ...typeCustomAttributes[index],
                    value: newValue,
                };
            } else {
                typeCustomAttributes.push({ label, value: newValue });
            }
        }

        const updatedItem = { ...item, typeCustomAttributes };
        setItem(updatedItem);
    };

    const handleCustomAttributesChange = (e, type, maxDecimals = null) => {
        let customAttributes = item.customAttributes ? [...item.customAttributes] : [];
        const label = e.target.name;
        let newValue;

        if (maxDecimals !== null) {
            e.target.value = limitDecimals(e.target.value, maxDecimals);
        }

        switch (type) {
            case 'integer':
                newValue = e.target.value === '' ? '' : e.target.valueAsNumber;
                break;
            case 'boolean':
                newValue = e.target.checked;
                break;
            case 'decimal':
                newValue = parseFloat(e.target.value) || 0;
                break;
            default:
                newValue = e.target.value;
        }

        if (newValue === '' && (type === 'text' || type === 'integer' || type === 'lookup')) {
            customAttributes = customAttributes.filter(attr => attr.label !== label);
        } else {
            const index = customAttributes.findIndex(attr => attr.label === label);
            if (index !== -1) {
                customAttributes[index] = {
                    ...customAttributes[index],
                    value: newValue,
                };
            } else {
                customAttributes.push({ label, value: newValue });
            }
        }

        const updatedItem = { ...item, customAttributes: customAttributes };
        setItem(updatedItem);
    };

    const saveChanges = async () => {
        let itemToSave = { ...item }

        if (!itemToSave.isUpfront) itemToSave.upfrontSalesforceCode = null;
        if (!itemToSave.isRecurring) itemToSave.recurringSalesforceCode = null;

        // Re-derive salesforceCode from the remaining codes
        const upCode = itemToSave.upfrontSalesforceCode;
        const recCode = itemToSave.recurringSalesforceCode;
        if (upCode && recCode && upCode !== recCode) {
            itemToSave.salesforceCode = `${upCode} / ${recCode}`;
        } else {
            itemToSave.salesforceCode = upCode || recCode || null;
        }
        
        if (_.isEqual(itemToSave, originalItem)) {
            setBasketItemModalIsOpen(false);
            return
        }

        if (!validateItem(itemToSave)) return false;

        // Clear out costs and prices that aren't appropriate for the configured item recurrence.
        // If the fields are not supplied and they are needed, then default to 0
        itemToSave = {
            ...itemToSave,
            quantity: itemToSave.quantity || 0,
            upfrontCost: !itemToSave.isUpfront ? null : itemToSave.upfrontCost || 0,
            upfrontPrice: !itemToSave.isUpfront ? null : itemToSave.upfrontPrice || 0,
            recurringCost: !itemToSave.isRecurring ? null : itemToSave.recurringCost || 0,
            recurringPrice: !itemToSave.isRecurring ? null : itemToSave.recurringPrice || 0
        }

        setTypeCustomAttributesDefaultValues(itemToSave);

        const isNewItem = item.id === 0
        let auditChanges = [];

        let cleanItem = { ...item };
        let cleanOriginalItem = { ...originalItem };

        if (!_.isEqual(cleanItem, cleanOriginalItem) || isNewItem) {
            auditItemChanges(isNewItem, auditChanges)
        }

        if (isNewItem) {
            itemToSave.guid = crypto.randomUUID()
        }

        setItem(itemToSave);
        setOriginalItem(itemToSave); //update the original item in memory to compare with future changes
        onItemChange(itemToSave, isNewItem, auditChanges);

        setBasketItemModalIsOpen(false);
    }

    const validateItem = (item) => {
        let blankFields = []
        if (!item.internalName) blankFields.push("Internal Name");
        if (!item.quoteGroupDescription) blankFields.push("Group Name");
        if (!item.itemLookupTypeId) blankFields.push("Type");
        if (!item.supplierId) blankFields.push("Supplier");
        if (item.isUpfront && !item.upfrontSalesforceCode) blankFields.push("Upfront Salesforce Code");
        if (item.isRecurring && !item.recurringSalesforceCode) blankFields.push("Recurring Salesforce Code");
        if (item.isUpfront && !item.upfrontNameOnQuote) blankFields.push("Upfront Name on Quote");
        if (item.isRecurring && !item.recurringNameOnQuote) blankFields.push("Recurring Name on Quote");

        if (selectedItemLookupType?.customAttributes)
            selectedItemLookupType.customAttributes.forEach(attribute => {
                var foundLabelInCustomAttributes = item.typeCustomAttributes.find(w => w.label === attribute.label)
                if (attribute.required && !foundLabelInCustomAttributes) {
                    if (attribute.defaultValue !== null) {
                        item.typeCustomAttributes.push({ label: attribute.label, value: attribute.defaultValue })
                    } else {
                        if (attribute.fieldType === "boolean")
                            item.typeCustomAttributes.push({ label: attribute.label, value: false })
                        else
                            blankFields.push(attribute.label);
                    }
                }
            });

        if (blankFields.length > 0) {
            toast.warning(`Please fill in the following fields: ${blankFields.join(", ")}`);
            return false
        }

        if (item.isUpfront === false && item.isRecurring === false) {
            toast.warning(`Please select whether this item is upfront and/or recurring (Commercials tab).`);
            return false
        }

        return true

    }

    const setTypeCustomAttributesDefaultValues = (itemToSave) => {
        const typeCustomAttributesWithDefaultValues = itemLookupTypesData.filter(w => w.id === itemToSave.itemLookupTypeId)[0].customAttributes?.filter(w => w.defaultValue)
        if (!typeCustomAttributesWithDefaultValues) return;

        typeCustomAttributesWithDefaultValues.forEach(typeAttribute => {
            const itemTypeCustomAttribute = itemToSave.typeCustomAttributes?.find(w => w.label === typeAttribute.label)
            if (!itemTypeCustomAttribute) {
                itemToSave.typeCustomAttributes.push({ label: typeAttribute.label, value: typeAttribute.defaultValue })
            }
            else if (itemTypeCustomAttribute.value === null || itemTypeCustomAttribute.value === undefined) {
                itemTypeCustomAttribute.value = typeAttribute.defaultValue
            }
        })

        setOriginalItem(itemToSave);
    }

    const stringifyCustomAttributes = (customAttributes, keyProp, valueProp) => {
        if (!customAttributes || customAttributes.length === 0) return null;

        customAttributes = typeof (customAttributes) === "string" ? JSON.parse(customAttributes) : customAttributes;

        customAttributes = customAttributes.reduce((acc, customAttributes) => {
            const key = String(customAttributes[keyProp]);
            acc[key] = customAttributes[valueProp];
            return acc;
        }, {});

        return JSON.stringify(customAttributes);
    }

    const auditItemChanges = (isNewItem, auditChanges) => {
        const auditFieldsToCompare = ["internalName", "quoteGroupDescription", "upfrontNameOnQuote", "recurringNameOnQuote", "options", "itemLookupTypeName", "supplierName", "autotaskCode", "salesforceCode", "isInternal",
            "notes", "customAttributes", "typeCustomAttributes", "disabled"]
        auditFieldsToCompare.forEach((field) => {
            let fieldDisplayName = field
            if (field === "itemLookupTypeName") {
                fieldDisplayName = "type";
            } else if (field === "supplierName") {
                fieldDisplayName = "supplier";
            }
            let originalFieldValue, currentFieldValue

            if (field === "customAttributes") {
                originalFieldValue = stringifyCustomAttributes(originalItem[field], "label", "value")
                currentFieldValue = stringifyCustomAttributes(item[field], "label", "value")
            }
            else if (field === "typeCustomAttributes") {
                originalFieldValue = stringifyCustomAttributes(originalItem[field], "label", "value")
                currentFieldValue = stringifyCustomAttributes(item[field], "label", "value")
            }
            else {
                originalFieldValue = originalItem[field] === null ? null : JSON.stringify(originalItem[field])
                currentFieldValue = item[field] === null ? null : JSON.stringify(item[field])
            }

            if (originalFieldValue === null && currentFieldValue === null) return;

            if (!isNewItem) {
                if (currentFieldValue !== originalFieldValue) {
                    var changes = `\"${fieldDisplayName}\": { \"new\": ${currentFieldValue}, \"old\": ${originalFieldValue} }`
                    auditChanges.push(changes)
                }
            } else {
                if (currentFieldValue !== null) {
                    auditChanges.push(`\"${fieldDisplayName}\": ${currentFieldValue}`)
                }
            }
        })
    }


    const onCustomAttributesChange = (newCustomAttributes) => {
        setItem(prevItem => ({
            ...prevItem,
            customAttributes: newCustomAttributes
        }));
    }

    // Define History columns
    const historyColumns = [
        {
            key: "timestamp",
            label: "Timestamp"
        },
        { key: "user", label: "User" },
        { key: "action", label: "Action" },
        { key: "notes", label: "Notes" },
        { key: "payload", label: "Payload" }
    ];

    const handleClose = () => {
        if (!_.isEqual(item, originalItem)) {
            openDialogBox();
        } else {
            setBasketItemModalIsOpen(false)
        }
    }

    const openDialogBox = () => setIsDialogBoxOpen(true);
    const closeDialogBox = () => setIsDialogBoxOpen(false);

    const getTypeCustomAttributeValue = (field) => {
        const typeCustomAttributes = item.typeCustomAttributes?.find(attr => attr.label === field.label)
        return typeCustomAttributes ? typeCustomAttributes.value : field.defaultValue || null;
    }

    const getCustomAttributeValue = (field) => {
        const customAttributes = item.customAttributes?.find(attr => attr.label === field.label)
        return customAttributes ? customAttributes.value : field.defaultValue || null;
    }

    return (
        <Modal isOpen={basketItemModalIsOpen} centered size="xl" scrollable backdrop="static">
            <ModalHeader toggle={() => setBasketItemModalIsOpen(!basketItemModalIsOpen)}>
                {item.id === 0
                    ? "New Basket Item"
                    : `Basket Item: ${originalItem.internalName}`
                }
            </ModalHeader>
            <ModalBody style={{ height: '60vh' }}>
                {/* Navigation */}
                <Row>
                    <Col style={{ display: 'inline-flex' }}>
                        {([
                            "Details", "Notes", "Commercials",
                            //...(!item.itemLookupId ? ["Commercials"] : []), 
                            ...(!selectedItemLookupType || !selectedItemLookupType?.customAttributes?.length ? [] : [selectedItemLookupType.itemLookupTypeName]),
                            , ...(item?.customAttributes && item.customAttributes?.length > 0 ? ["Custom"] : []),
                            , "Debug"]).map((tab) => (
                                <h6
                                    key={tab}
                                    className={currentActiveTab === tab ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setCurrentActiveTab(tab)}
                                >
                                    {tab}
                                </h6>
                            ))}
                    </Col>
                    <Col className="text-end">
                        <h6
                            className={currentActiveTab === "History" ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                            style={{ cursor: 'pointer', display: 'inline-flex' }}
                            onClick={() => setCurrentActiveTab("History")}
                        >
                            History
                        </h6>
                    </Col>
                </Row>
                <br />

                {/* Details Tab */}
                {currentActiveTab === "Details" && (
                    <Container>
                        {item.itemLookupId === null &&
                            <FormGroup row>
                                <Label for="type" sm={2}>Type</Label>
                                <Col sm={10}>
                                    <CustomSelect
                                        id="type"
                                        name="itemLookupTypeId"
                                        isSearchable={true}
                                        options={
                                            [...itemLookupTypeOptions].sort((a, b) =>
                                                a.label.toLowerCase().localeCompare(b.label.toLowerCase())
                                            )
                                        }
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            ...customStyles,
                                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                                            menu: base => ({ ...base, zIndex: 9999 })
                                        }}
                                        classNamePrefix="data-select"
                                        value={item.itemLookupTypeId}
                                        onChange={handleChange}
                                        isDisabled={false}
                                    />
                                    {!item.itemLookupTypeId && (
                                        <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                            Type is required.
                                        </div>
                                    )}
                                </Col>
                            </FormGroup>
                        }

                        <FormGroup row>
                            <Label for="name" sm={2}>Internal Name</Label>
                            <Col sm={10}>
                                <Input
                                    type="text"
                                    name="internalName"
                                    id="internalName"
                                    value={item.internalName || ""}
                                    onChange={handleChange}
                                    invalid={!item.internalName}
                                />
                                <FormFeedback>Internal Name is required.</FormFeedback>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label for="name" sm={2}>Group Name</Label>
                            <Col sm={10}>
                                <Input
                                    type="text"
                                    name="quoteGroupDescription"
                                    id="quoteGroupDescription"
                                    value={item.quoteGroupDescription || ""}
                                    onChange={handleChange}
                                    invalid={!item.quoteGroupDescription}
                                />
                                <FormFeedback>Group name is required.</FormFeedback>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label sm={2}>Quantity</Label>
                            <Col sm={2}>
                                <FormattedNumberField
                                    name="quantity"
                                    numDecimalPlaces={0}
                                    value={item.quantity}
                                    onChange={(e) => handleChange(e)}
                                    invalid={itemErrors.find(x => x.field === "quantity")}

                                />
                                <FormFeedback>{itemErrors.find(x => x.field === "quantity")?.error}</FormFeedback>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label for="supplierId" sm={2}>Supplier</Label>
                            <Col sm={10}>
                                <CustomSelect
                                    id="supplierId"
                                    name="supplierId"
                                    isSearchable={true}
                                    options={
                                        [...suppliersData || []].map(x => ({ value: x.id, label: x.supplierName })).sort((a, b) =>
                                            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
                                        )
                                    }
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        ...customStyles,
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        menu: base => ({ ...base, zIndex: 9999 })
                                    }}
                                    value={item.supplierId}
                                    onChange={handleChange}
                                    isDisabled={item.itemLookupId !== null && item.supplierId !== null}
                                />
                                {!item.supplierId && (
                                    <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                        Supplier is required.
                                    </div>
                                )}
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label for="autotaskCode" sm={2}>Autotask Code</Label>
                            <Col sm={10}>
                                <Input
                                    type="text"
                                    name="autotaskCode"
                                    id="autotaskCode"
                                    value={item.autotaskCode || ""}
                                    onChange={handleChange}
                                    disabled={item.itemLookupId !== null}
                                />
                            </Col>
                        </FormGroup>

                    </Container>
                )}

                {selectedItemLookupType && currentActiveTab === "Notes" &&
                    <Container>
                        <FormGroup row>
                            <Label sm={2}>Notes</Label>
                            <Col sm={10}>
                                <Input
                                    type="textarea"
                                    name="notes"
                                    value={item.notes}
                                    onChange={handleChange}
                                />
                            </Col>
                        </FormGroup>
                    </Container>
                }

                {selectedItemLookupType && currentActiveTab === selectedItemLookupType.itemLookupTypeName &&
                    <Container>
                        {selectedItemLookupType?.customAttributes && selectedItemLookupType.customAttributes?.length && selectedItemLookupType.customAttributes
                            .map((field, index) => {
                            return (
                                <FormGroup row>
                                    <Label for="type" sm={2}>{field.label}</Label>
                                    <Col sm={10}>
                                        <ItemTypeCustomAttributes key={index} label={field.label} type={field.fieldType} required={field.required} handleChange={handleTypeCustomAttributesChange} value={getTypeCustomAttributeValue(field)} lookupChoices={field.lookupChoices} />
                                    </Col>
                                </FormGroup>
                            );
                        })}
                    </Container>
                }

                {/* Basics Tab */}
                {currentActiveTab === "Commercials" && (
                    <Container>
                        <Row>
                            <Col>
                                <Row className="my-3">
                                    <Col>
                                        <h6 className="text-primary">Upfront</h6>
                                    </Col>
                                    <Col sm={8}>
                                        <Input
                                            name="isUpfront"
                                            type="checkbox"
                                            disabled={item.itemLookupId}
                                            checked={item.isUpfront}
                                            onChange={(e) => handleChange(e)}
                                        />
                                    </Col>
                                </Row>

                                <FormGroup row style={item.isUpfront ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Quote Description</Label>
                                    <Col sm={8}>
                                        <TextInput name="upfrontNameOnQuote" value={item.upfrontNameOnQuote} isEditable={true} onChange={(e) => handleChange(e)} />
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isUpfront ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Salesforce Code</Label>
                                    <Col sm={8}>
                                            <CustomSelect
                                                name="upfrontSalesforceCode"
                                                isSearchable
                                                options={upfrontSalesforceCodes.map((item) => ({
                                                    value: item.name,
                                                    label: item.type === "Upfront" ? item.label ?? item.name : item.name + " 🔁",
                                                    //label: item.replace('COE_', '').replace('__c', '')
                                                }))}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    ...customStyles,
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                    menu: base => ({ ...base, zIndex: 9999 })
                                                }}
                                                classNamePrefix="data-select"
                                                onChange={handleUpfrontSalesforceCodeChange}
                                                value={item.upfrontSalesforceCode?.trim()}
                                                isDisabled={item.itemLookupId !== null && item.upfrontSalesforceCode !== null}
                                                invalid={itemErrors.find(x => x.field === "upfrontSalesforceCode")}
                                            />
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isUpfront ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Cost</Label>
                                    <Col sm={8}>
                                        <FormattedNumberField
                                            id="upfrontCost"
                                            name={originalItem.id === 0 ? "unlocked_upfrontCost" : "upfrontCost"} // new items don't have locked costs
                                            numDecimalPlaces={12}
                                            value={item.upfrontCost}
                                            isEditable={true}
                                            onChange={(e) => handleChange(e)}
                                            style={{ width: "150px" }}
                                            errorMessage={itemErrors.find(x => x.field === "upfrontCost")?.error}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isUpfront ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Price</Label>
                                    <Col sm={8}>
                                        <FormattedNumberField
                                            id="upfrontPrice"
                                            name="upfrontPrice"
                                            numDecimalPlaces={2}
                                            value={item.upfrontPrice}
                                            isEditable={true}
                                            onChange={(e) => handleChange(e)}
                                            style={{ width: "150px" }}
                                            errorMessage={itemErrors.find(x => x.field === "upfrontPrice")?.error}
                                        />
                                    </Col>
                                </FormGroup>

                                {   sfCodes.find(code => code.name === item.upfrontSalesforceCode && code.type === "Recurring")?.canBePaidUpfront &&
                                    <FormGroup row style={item.isUpfront ? {} : { visibility: "hidden" }}>
                                        <Label sm={4}>Recognition Period (Months)</Label>
                                        <Col>
                                            <FormattedNumberField
                                                id="upfrontRecognitionMonths"
                                                name="upfrontRecognitionMonths"
                                                numDecimalPlaces={0}
                                                value={item.upfrontRecognitionMonths}
                                                isEditable={true}
                                                onChange={(e) => handleChange(e)}
                                                invalid={itemErrors.find(x => x.field === "upfrontRecognitionMonths")}
                                                style={{ width: "75px" }}
                                            />
                                        </Col>
                                        <Row>
                                            <Col sm={4} className="text-muted" style={{ fontSize: "0.875rem" }}>
                                                
                                            </Col>
                                            <Col>
                                                {itemErrors.find(x => x.field === "upfrontRecognitionMonths") && (
                                                    <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                                        {itemErrors.find(x => x.field === "upfrontRecognitionMonths")?.error}
                                                    </div>
                                                )}

                                                <div className="text-muted m-0 p-2" style={{ fontSize: "0.875rem" }}>
                                                    <i className="bi bi-info-circle-fill me-2"></i>Number of months over which upfront revenue is recognised in Salesforce
                                                </div>

                                            </Col>
                                        </Row>

                                    </FormGroup>
                                }
                            </Col>
                            <Col>
                                <Row className="my-3">
                                    <Col sm={4}>
                                        <h6 className="text-primary">Recurring</h6>
                                    </Col>
                                    <Col sm={2}>
                                        <Input
                                            name="isRecurring"
                                            type="checkbox"
                                            disabled={item.itemLookupId}
                                            checked={item.isRecurring}
                                            onChange={(e) => handleChange(e)}
                                        />
                                    </Col>
                                </Row>

                                <FormGroup row style={item.isRecurring ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Quote Description</Label>
                                    <Col sm={8}>
                                        <TextInput
                                            name="recurringNameOnQuote"
                                            value={item.recurringNameOnQuote}
                                            isEditable={true}
                                            onChange={(e) => handleChange(e)}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isRecurring ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Salesforce Code</Label>
                                    <Col sm={8}>
                                                                                <CustomSelect
                                                name="recurringSalesforceCode"
                                                isSearchable
                                                options={recurringSalesforceCodes.map((item) => ({
                                                    value: item.name,
                                                    label: item.label ?? item.name,
                                                    //label: item.replace('COE_', '').replace('__c', '')
                                                }))}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    ...customStyles,
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                    menu: base => ({ ...base, zIndex: 9999 })
                                                }}
                                                classNamePrefix="data-select"
                                                onChange={handleRecurringSalesforceCodeChange}
                                                value={item.recurringSalesforceCode?.trim()}
                                                isDisabled={item.itemLookupId !== null && item.recurringSalesforceCode !== null}
                                                invalid={itemErrors.find(x => x.field === "recurringSalesforceCode")}
                                                />
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isRecurring ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Cost</Label>
                                    <Col>
                                        <FormattedNumberField
                                            id="recurringCost"
                                            name={originalItem.id === 0 ? "unlocked_recurringCost" : "recurringCost"} // new items don't have locked costs
                                            numDecimalPlaces={12}
                                            value={item.recurringCost}
                                            isEditable={true}
                                            onChange={(e) => handleChange(e)}
                                            style={{ width: "150px" }}
                                            errorMessage={itemErrors.find(x => x.field === "recurringCost")?.error}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isRecurring ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Price</Label>
                                    <Col>
                                        <FormattedNumberField
                                            id="recurringPrice"
                                            name="recurringPrice"
                                            value={item.recurringPrice}
                                            numDecimalPlaces={2}
                                            isEditable={true}
                                            onChange={(e) => handleChange(e)}
                                            style={{ width: "150px" }}
                                            errorMessage={itemErrors.find(x => x.field === "recurringPrice")?.error}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isRecurring ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Start Month<br /></Label>
                                    <Col>
                                        <FormattedNumberField
                                            id="recurringStartMonth"
                                            name="recurringStartMonth"
                                            numDecimalPlaces={0}
                                            value={item.recurringStartMonth}
                                            isEditable={true}
                                            onChange={(e) => handleChange(e)}
                                            invalid={itemErrors.find(x => x.field === "recurringStartMonth")}
                                            style={{ width: "75px" }}
                                        />
                                        {itemErrors.find(x => x.field === "recurringStartMonth") && (
                                            <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                                {itemErrors.find(x => x.field === "recurringStartMonth")?.error}
                                            </div>
                                        )}
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isRecurring ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Interval (Months)</Label>
                                    <Col>
                                        <FormattedNumberField
                                            id="recurringIntervalMonths"
                                            name="recurringIntervalMonths"
                                            numDecimalPlaces={0}
                                            value={item.recurringIntervalMonths}
                                            isEditable={true}
                                            onChange={(e) => handleChange(e)}
                                            invalid={itemErrors.find(x => x.field === "recurringIntervalMonths")}
                                            style={{ width: "75px" }}
                                        />
                                        {itemErrors.find(x => x.field === "recurringIntervalMonths") && (
                                            <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                                {itemErrors.find(x => x.field === "recurringIntervalMonths")?.error}
                                            </div>
                                        )}
                                    </Col>
                                </FormGroup>

                                <FormGroup row style={item.isRecurring ? {} : { visibility: "hidden" }}>
                                    <Label sm={4}>Num Payments</Label>
                                    <Col>
                                        <FormattedNumberField
                                            id="recurringNumPayments"
                                            name="recurringNumPayments"
                                            numDecimalPlaces={0}
                                            value={item.recurringNumPayments}
                                            isEditable={true}
                                            onChange={(e) => handleChange(e)}
                                            invalid={itemErrors.find(x => x.field === "recurringNumPayments")}
                                            style={{ width: "75px" }}
                                        />
                                        {itemErrors.find(x => x.field === "recurringNumPayments") && (
                                            <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                                {itemErrors.find(x => x.field === "recurringNumPayments")?.error}
                                            </div>
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                        </Row>

                        <hr />

                        <FormGroup row>
                            <Label sm={2}>Is Internal?</Label>
                            <Col xs="auto">
                                <Input
                                    name="isInternal"
                                    type="checkbox"
                                    disabled={item.itemLookupId}
                                    checked={item.isInternal}
                                    onChange={(e) => handleChange(e)}
                                />
                            </Col>
                            <Col>
                                <FormText sm={8}><i className="bi bi-info-circle-fill me-2"></i>If checked, then this is a Maintel Item. Otherwise, it's from a third party supplier</FormText>
                            </Col>
                        </FormGroup>

                        <FormGroup row>

                            <Label sm={2}>Is Credit Item?</Label>
                            <Col xs="auto">
                                <Input
                                    name="isCreditItem"
                                    type="checkbox"
                                    checked={item.isCreditItem}
                                    onChange={(e) => handleChange(e)}
                                />
                            </Col>
                            <Col>
                                <FormText sm={8}><i className="bi bi-info-circle-fill me-2"></i>If checked, this will produce negative quantities and costs/prices.  Typically used for changing an order, or for refunds</FormText>
                            </Col>
                        </FormGroup>


                        {/*
                        isUpfront: currentVersion ? currentVersion.isUpfront : false,
                        quoteUpfrontDescription: currentVersion ? currentVersion.quoteUpfrontDescription : null,
                        upfrontCost: currentVersion ? currentVersion.upfrontCost : null,
                        upfrontPrice: currentVersion ? currentVersion.upfrontPrice : null,
                        upfrontCostDefaultDiscount: currentVersion ? currentVersion.upfrontCostDefaultDiscount : null,
                        upfrontCostDiscountThreshold: currentVersion ? currentVersion.upfrontCostDiscountThreshold : null,
                        upfrontPriceDefaultDiscount: currentVersion ? currentVersion.upfrontPriceDefaultDiscount : null,
                        upfrontPriceDiscountThreshold: currentVersion ? currentVersion.upfrontPriceDiscountThreshold : null,
                        isRecurring: currentVersion ? currentVersion.isRecurring : false,
                        quoteRecurringDescription: currentVersion ? currentVersion.quoteRecurringDescription : null,
                        recurringCost: currentVersion ? currentVersion.recurringCost : null,
                        recurringPrice: currentVersion ? currentVersion.recurringPrice : null,
                        recurringCostDefaultDiscount: currentVersion ? currentVersion.recurringCostDefaultDiscount : null,
                        recurringCostDiscountThreshold: currentVersion ? currentVersion.recurringCostDiscountThreshold : null,
                        recurringPriceDefaultDiscount: currentVersion ? currentVersion.recurringPriceDefaultDiscount : null,
                        recurringPriceDiscountThreshold: currentVersion ? currentVersion.recurringPriceDiscountThreshold : null,
                        activeStartDate: new Date(),
                        createdAt: new Date(),
                        updatedAt: null,
                        disabled: false
                        */}

                    </Container>
                )}

                {/* Custom Attributes Tab */}
                {currentActiveTab === "Custom" && (
                    <Container>
                        {item?.customAttributes && item.customAttributes?.length > 0 && item.customAttributes.map((field, index) => {
                            return (
                                <FormGroup row>
                                    <Label for="type" sm={2}>{field.label}</Label>
                                    <Col sm={10}>
                                        <ItemTypeCustomAttributes key={index} label={field.label} type={field.fieldType} required={field.required} handleChange={handleCustomAttributesChange} value={getCustomAttributeValue(field)} lookupChoices={field.lookupChoices} />
                                    </Col>
                                </FormGroup>
                            );
                        })}
                    </Container>
                )}

                {currentActiveTab === "Debug" && (
                    <div style={{ height: "calc(100% - 120px)", display: "flex", flexDirection: "column" }}>
                        <Input
                            type="textarea"
                            value={JSON.stringify(item, null, 2)}
                            style={{ height: "100%", resize: "none" }}
                        />
                    </div>
                )}

                {/* History Tab */}
                {currentActiveTab === "History" && (
                    <Container>
                        <DataTable
                            className="mb-5 pb-5"
                            columns={historyColumns}
                            data={auditData ?? []}
                            exportToCsv={true}
                            csvFileName="audit_items.csv"
                            sortByColumn="id"
                            sortDirection="descending"
                            isLoading={isLoadingAudits || isLoadingUsersLookup}
                            error={auditError || usersLookupError}
                            searchableFields={["timestamp", "user", "action", "notes", "payload"]}
                        />
                        <br />
                        <br />
                    </Container>
                )}
            </ModalBody>
            <ModalFooter>
                <Button outline onClick={handleClose} color={theme === "dark-mode" ? "dark" : "light"}>
                    Close
                </Button>
                <Button color="secondary" onClick={saveChanges}>Save</Button>
            </ModalFooter>

            <DialogBox
                isOpen={isDialogBoxOpen}
                title="Confirmation"
                message={
                    <div className="d-flex align-items-start">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-1 me-3"></i>
                        <span>You have unsaved changes. <br />Save changes before closing?</span>
                    </div>
                }
                toggle={closeDialogBox}
                buttons={[
                    {
                        label: 'Yes',
                        color: 'primary',
                        // icon: 'bi bi-check2-circle',
                        onClick: () => { saveChanges(); closeDialogBox(); }
                    },
                    {
                        label: 'No',
                        color: 'secondary',
                        // icon: 'bi bi-x-circle',
                        onClick: () => { closeDialogBox(); setBasketItemModalIsOpen(!basketItemModalIsOpen); }
                    },
                    {
                        label: 'Cancel',
                        color: 'secondary',
                        onClick: () => { closeDialogBox(); }
                    }
                ]}
            />
        </Modal>
    );
};

export default BasketItemModal;
